<template>
  <section class="innovation-showcase">
    <div class="container">
      <div class="showcase-content">
        <!-- Left Side - Image Collage -->
        <div class="image-collage">
          <div class="image-grid">
            <div 
              v-for="(image, index) in showcaseImages" 
              :key="index"
              class="image-item wow fadeInLeft" 
              :class="`image-${index + 1}`"
              :data-wow-delay="`${(index + 1) * 0.1}s`"
            >
              <img :src="image.src" :alt="image.alt" />
            </div>
          </div>
          
          <!-- Decorative Elements -->
          <div class="decorative-squiggle">
            <img :src="decorativeElements.squiggle.src" :alt="decorativeElements.squiggle.alt" />
          </div>
          <div class="decorative-star">
            <img :src="decorativeElements.star.src" :alt="decorativeElements.star.alt" />
          </div>
        </div>

        <!-- Right Side - Content -->
        <div class="content-block">
          <h2 class="showcase-title" v-html="contentData.title"></h2>
          <p 
            v-for="(description, index) in contentData.descriptions" 
            :key="index"
            class="showcase-description"
            v-html="description"
          ></p>
          <div class="btn-box">
            <router-link :to="contentData.buttonLink" class="showcase-btn">
              <i :class="contentData.buttonIcon"></i> {{ contentData.buttonText }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "WhySogdoItsInnovationComp",
  props: {
    showcaseImages: {
      type: Array,
      required: true,
      default: () => []
    },
    decorativeElements: {
      type: Object,
      required: true,
      default: () => ({
        squiggle: { src: "/shapes/shape-04.png", alt: "Squiggle" },
        star: { src: "/shapes/shape-05.png", alt: "Star" }
      })
    },
    contentData: {
      type: Object,
      required: true,
      default: () => ({
        title: "Discover why <span class=\"highlight-pink\">Sogdo</span> is your <span class=\"highlight-pink\">perfect</span> choice",
        descriptions: [
          "Experience the power of <strong>Sogdo's expertise</strong> in digital transformation. Our team combines cutting-edge technology with proven methodologies to deliver solutions that drive real business growth and sustainable success for companies across various industries.",
          "From innovative web development to comprehensive digital strategies, Sogdo transforms your vision into reality. Join hundreds of satisfied clients who trust us for their technological advancement and business excellence."
        ],
        buttonText: "Learn More",
        buttonLink: "/contact",
        buttonIcon: "icon-02"
      })
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Innovation Showcase Section */
.innovation-showcase {
  padding: 120px 0;
  background: #fff;
  position: relative;
}

.showcase-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

/* Left Side - Image Collage */
.image-collage {
  position: relative;
  height: 500px;
}

.image-grid {
  position: relative;
  width: 100%;
  height: 100%;
}

.image-item {
  position: absolute;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.image-item:hover {
  opacity: 0.8;
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-1 {
  top: 0;
  left: 0;
  width: 44%;
  height: 46%;
  z-index: 3;
  border-top-right-radius: 50px;
}

.image-2 {
  bottom: 41px;
  left: 0;
  width: 45%;
  height: 40%;
  z-index: 2;
  border-top-right-radius: 50px;
}

.image-3 {
  top: 46%;
  right: 15%;
  width: 35%;
  height: 81%;
  z-index: 1;
  transform: translateY(-50%);
  border-top-left-radius: 100px;
  border-bottom-left-radius: 50px;
}

@media (max-width: 768px) {
  .image-3 {
    right: 15%;
  }
}

/* Decorative Elements */
.decorative-squiggle {
  position: absolute;
  bottom: 10%;
  left: 20%;
  z-index: 4;
  opacity: 0.8;
}

.decorative-squiggle img {
  width: 80px;
  height: auto;
  filter: brightness(0) saturate(100%) invert(60%) sepia(100%) saturate(1000%) hue-rotate(200deg);
}

.decorative-star {
  position: absolute;
  bottom: 5%;
  right: 15%;
  z-index: 4;
  opacity: 0.9;
}

.decorative-star img {
  width: 40px;
  height: auto;
  filter: brightness(0) saturate(100%) invert(85%) sepia(100%) saturate(1000%) hue-rotate(45deg);
}

/* Right Side - Content */
.content-block {
  padding-left: 40px;
}

.showcase-title {
  font-size: 48px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 30px;
  color: #2c3e50;
  font-family: 'Archivo', sans-serif;
}

.highlight-pink {
  color: #FB0E76;
  font-weight: 700;
}

.showcase-description {
  font-size: 18px;
  line-height: 1.7;
  color: #6c757d;
  margin-bottom: 25px;
}

.showcase-description strong {
  color: #2c3e50;
  font-weight: 600;
}

/* Showcase Button - Matching Hero Button */
.showcase-btn {
  padding: 12px 30px;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 0;
  background: linear-gradient(219deg, #FB0E76 0%, #910694 100%);
  color: #fff;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.showcase-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(251, 14, 118, 0.4);
  color: #fff;
  text-decoration: none;
}

.showcase-btn i {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.showcase-btn:hover i {
  transform: translateX(3px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .showcase-content {
    grid-template-columns: 1fr;
    gap: 60px;
  }
  
  .content-block {
    padding-left: 0;
    text-align: center;
  }
  
  .showcase-title {
    font-size: 36px;
  }
  
  .image-collage {
    height: 400px;
  }
}

@media (max-width: 480px) {
  .showcase-title {
    font-size: 32px;
  }
  
  .showcase-description {
    font-size: 16px;
  }
  
  .image-collage {
    height: 350px;
  }
}
</style>
